import { Pencil2Icon } from '@radix-ui/react-icons'
import { Link } from 'react-router'
import {
	eventEnd,
	eventSummary,
	formatClockSpan,
	formatWeekday,
	isEventPassed,
	parseSafeZone,
	performersDisplay,
	safeZoneImageStyle,
	type ProgramEvent,
	venueLabel,
} from '#app/domains/program/pure'
import { isFreeEvent, pricesOf } from '#app/domains/pricing/pure'
import { isSoldOut, seatsTaken } from '#app/domains/tickets/pure'
import { useRevealOnce } from '#app/utils/motion.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { SeatMeter } from '../../molecules/ticket/SeatMeter.tsx'

function StatusStamp({ text }: { text: string }) {
	return (
		<span className="ec-stamp">
			<span aria-hidden="true">{text}</span>
			<span className="sr-only">{text}</span>
		</span>
	)
}

function TicketGlyph() {
	return (
		<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
			<path
				d="M2 4.5h12v2a1.5 1.5 0 0 0 0 3v2H2v-2a1.5 1.5 0 0 0 0-3z"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinejoin="round"
			/>
			<path d="M10 5v6" stroke="currentColor" strokeDasharray="1.2 1.3" />
		</svg>
	)
}

const isFreeConcert = (event: ProgramEvent) =>
	event.adultPrice === 0 && isFreeEvent(pricesOf(event))

/**
 * A concert in the program. The whole card is one link to the concert page
 * (cards are for browsing, the detail page is for acting); it shows the
 * facts that help choose: time, who, where (with the map pin number) and how
 * many seats are left — or, at a drop-in event, that nobody needs a ticket.
 */
export function EventCard({
	event,
	pinNumber,
	active = false,
	mine = false,
	inCart = false,
}: {
	event: ProgramEvent
	/** the venue's number on the program map, when the map is shown */
	pinNumber?: number | null
	/** highlighted, e.g. while its venue is lit on the map */
	active?: boolean
	/** the visitor holds tickets for it */
	mine?: boolean
	/** …only in the cart yet: not settled in Kassen */
	inCart?: boolean
}) {
	const user = useOptionalUser()
	const canEdit = user?.roles.some((role) =>
		role.permissions.some(
			(p) =>
				p.action === 'update' && p.entity === 'event' && p.access === 'any',
		),
	)
	const [ref, reveal] = useRevealOnce<HTMLElement>({ threshold: 0.35 })

	const passed = isEventPassed(eventEnd(event))
	const soldOut = !passed && isSoldOut(event)
	const total = event.seats?.total ?? 0
	const taken = seatsTaken(event)
	const free = Math.max(0, total - taken)
	const summary = eventSummary(event)
	const venue = venueLabel(event)
	const performers = performersDisplay({
		performers: event.performers ?? null,
		featuring: event.featuring ?? [],
	})
	const clock = formatClockSpan(event.date, event.endsAt)
	const when = `${formatWeekday(event.date)} kl. ${clock}`
	const seatText = passed
		? 'fullført'
		: event.dropIn
			? 'treng ikkje billett, berre møt opp'
			: soldOut
				? 'utseld'
				: total > 0
					? `${free} av ${total} plassar ledige`
					: null

	return (
		<article
			ref={ref}
			id={`event-${event.id}`}
			className="ec-card"
			data-state={passed ? 'passed' : soldOut ? 'soldout' : 'open'}
			data-active={active ? '' : undefined}
			data-reveal={reveal}
		>
			<Link
				to={`/events/${event.id}`}
				viewTransition
				className="ec-main"
				aria-label={[
					event.title,
					when,
					venue,
					mine ? (inCart ? 'billettar i korga' : 'du har billett') : null,
					seatText,
				]
					.filter(Boolean)
					.join(', ')}
			>
				<div className="ec-top">
					<time
						className="ec-time"
						dateTime={new Date(event.date).toISOString()}
					>
						{clock}
					</time>
					<span className="ec-flags">
						{mine && !passed ? (
							inCart ? (
								<span className="ec-badge ec-badge-cart">I korga</span>
							) : (
								<span className="ec-badge ec-badge-mine">
									<TicketGlyph /> Du har billett
								</span>
							)
						) : null}
						{passed ? (
							<StatusStamp text="Fullført" />
						) : soldOut ? (
							<StatusStamp text="Utseld" />
						) : event.dropIn ? (
							<span className="ec-badge">Berre møt opp</span>
						) : isFreeConcert(event) ? (
							<span className="ec-badge">Gratis</span>
						) : null}
					</span>
				</div>

				<div className="ec-head">
					<div className="ec-head-text">
						<h3
							className="ec-title"
							// flies into the concert page's hero title
							style={{ viewTransitionName: `event-title-${event.id}` }}
						>
							{event.title}
						</h3>
						{performers ? <p className="ec-performers">{performers}</p> : null}
					</div>
					{event.imageUrl ? (
						<img
							src={event.imageUrl}
							alt=""
							width={72}
							height={72}
							loading="lazy"
							decoding="async"
							className="ec-thumb"
							style={safeZoneImageStyle(parseSafeZone(event.imageSafeZone), 1)}
						/>
					) : null}
				</div>

				{summary ? <p className="ec-summary">{summary}</p> : null}

				<div className="ec-foot">
					{venue ? (
						<p className="ec-venue">
							{pinNumber ? (
								<span className="ec-pin" aria-hidden="true">
									{pinNumber}
								</span>
							) : (
								<span className="ec-pin ec-pin-blank" aria-hidden="true" />
							)}
							<span>{venue}</span>
						</p>
					) : null}
					{!passed && total > 0 ? (
						<SeatMeter total={total} taken={taken} className="ec-seats" />
					) : null}
				</div>
				<span className="ec-go" aria-hidden="true">
					<svg viewBox="0 0 24 24" width="18" height="18">
						<path
							d="M5 12h13m-5-6 6 6-6 6"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</span>
			</Link>
			{event.id && canEdit ? (
				<Link
					to={`/events/${event.id}/edit`}
					className="ec-edit"
					aria-label={`Rediger ${event.title}`}
				>
					<Pencil2Icon className="h-4 w-4" />
				</Link>
			) : null}
		</article>
	)
}
